import { ComponentPropsWithoutRef, PropsWithChildren } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { createSoftCredit, deleteSoftCreditById, updateSoftCreditById } from 'api/transactions';
import { transformSoftCreditToUpstream } from 'assets/js/pages/transactions/soft-credits/transforms';
import { SoftCreditFormValues } from 'assets/js/pages/transactions/soft-credits/types';
import * as Form from 'components/form';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { twMerge } from 'tailwind-merge';
import { SoftCredit, Transaction } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface SoftCreditFormProps extends PropsWithChildren {
    defaultSoftCredit?: SoftCredit;
    onSaveComplete?: (softCredit: SoftCredit) => void;
}
const SoftCreditForm = ({ onSaveComplete, defaultSoftCredit, children }: SoftCreditFormProps) => {
    const { accountId } = useCurrentAccount();
    const { data: transaction } = useCurrentTransactionOrById();
    const { success, error } = useToast();

    const defaultValues: SoftCreditFormValues = {
        softCredit: {
            selectedContact: defaultSoftCredit?.contact
                ? [defaultSoftCredit.contact.id.toString()]
                : [],
            firstName: '',
            lastName: '',
            companyName: '',
            contactType: ContactType.Individual,
            emails: [{ value: '', is_primary: true, type: 'personal' }],
            phones: [{ value: '', is_primary: true, type: 'home' }],
            amount: defaultSoftCredit?.amount ?? transaction.amount.toString(),
            note: defaultSoftCredit?.note ?? '',
        },
    };

    const methods = Form.useGivebutterForm<SoftCreditFormValues>({ defaultValues });
    const { handleSubmit, setError, resetFormButton } = methods;

    const { mutate } = useMutation<SoftCredit, GivebutterAxiosError, SoftCreditFormValues>({
        mutationFn: values => {
            const transformedValues = transformSoftCreditToUpstream(values.softCredit);
            if (defaultSoftCredit) {
                return updateSoftCreditById(
                    accountId,
                    transaction.id,
                    defaultSoftCredit.id,
                    transformedValues,
                );
            } else {
                return createSoftCredit(accountId, transaction.id, transformedValues);
            }
        },
        onSuccess: response => {
            success('Soft credit saved successfully!');
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, response.contact.id),
            });

            queryClient.setQueryData(
                transactionQueryKeys.transaction(accountId, transaction.id),
                (oldData: Transaction) => {
                    const updatedSoftCredits = defaultSoftCredit
                        ? oldData.soft_credits.map(item =>
                              item.id === response.id ? response : item,
                          )
                        : [...oldData.soft_credits, response];
                    return {
                        ...oldData,
                        soft_credits: updatedSoftCredits,
                    };
                },
            );
            onSaveComplete?.(response);
        },
        onError: e => {
            const data = e.response?.data;
            const errors = data?.errors;

            error(
                data?.message ??
                    `Failed to ${defaultSoftCredit ? 'updated' : 'create'} the soft credit.`,
            );

            if (typeof errors === 'object') {
                for (let field in errors) {
                    if (field in defaultValues.softCredit) {
                        setError(`softCredit.${field}` as keyof SoftCreditFormValues, {
                            message: errors[field][0],
                        });
                    }
                }
            }
            resetFormButton();
        },
    });

    return (
        <Form.Provider {...methods}>
            <form onSubmit={handleSubmit(values => mutate(values))}>
                <div className="flex flex-col gap-4">{children}</div>
            </form>
        </Form.Provider>
    );
};

interface SoftCreditFormActionsProps extends ComponentPropsWithoutRef<'div'> {}
const SoftCreditFormActions = ({ children, className }: SoftCreditFormActionsProps) => {
    return <div className={twMerge('flex justify-between', className)}>{children}</div>;
};

interface SoftCreditFormDeleteButtonProps {
    softCreditId: number;
    onDelete?: () => void;
}
const SoftCreditFormDeleteButton = ({
    softCreditId,
    onDelete,
}: SoftCreditFormDeleteButtonProps) => {
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();
    const { data: transaction } = useCurrentTransactionOrById();

    const { mutate: deleteSoftCredit } = useMutation<undefined, GivebutterAxiosError>({
        mutationFn: () => {
            return deleteSoftCreditById(accountId, transaction.id, softCreditId);
        },
        onSuccess: () => {
            success('Soft credit deleted successfully!');
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });
            queryClient.setQueryData(
                transactionQueryKeys.transaction(accountId, transaction.id),
                (oldData: Transaction) => {
                    return {
                        ...oldData,
                        soft_credits: oldData.soft_credits.filter(item => item.id !== softCreditId),
                    };
                },
            );
            onDelete?.();
        },
        onError: e => {
            error(e.response?.data?.message ?? 'Failed to delete the soft credit.');
        },
    });
    return (
        <Button
            variant="outline"
            color="neutral"
            className="!text-red-600"
            onClick={() => deleteSoftCredit()}
        >
            Delete credit
        </Button>
    );
};

const Root = SoftCreditForm;
const Actions = SoftCreditFormActions;
const DeleteButton = SoftCreditFormDeleteButton;

export {
    SoftCreditForm,
    SoftCreditFormActions,
    SoftCreditFormDeleteButton,
    //
    Root,
    Actions,
    DeleteButton,
};
